import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Container, Alert } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import ReservationDetailsPage from '../components/ReservationDetailsPage';
import { getTokenAuth } from '../utils/authUtils';

/**
 * Page standalone pour afficher les détails d'une réservation
 * Accessible via URL: /reservations/:id
 * Utilisé pour les liens emails et deep linking
 */
const ReservationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadedId, setLoadedId] = useState(null); // Pour éviter les rechargements

  useEffect(() => {
    // Ne charger que si l'ID change et qu'on ne l'a pas déjà chargé
    if (!id || loadedId === id) return;

    const fetchReservation = async () => {
      if (!user) {
        // Rediriger vers login si non authentifié
        navigate(`/login?redirect=/reservations/${id}`);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const token = getTokenAuth();
        const response = await fetch(`/api/v1/reservations/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Réservation introuvable');
          } else if (response.status === 403) {
            throw new Error('Vous n\'avez pas accès à cette réservation');
          } else {
            throw new Error('Erreur lors du chargement de la réservation');
          }
        }

        const data = await response.json();
        console.log('📦 Données reçues du backend:', data);
        
        // Le backend renvoie { success: true, data: { reservation: {...}, codes: [...], ... } }
        const responseData = data.data || data;
        
        // Extraire l'objet reservation de la structure
        const reservationData = responseData.reservation || responseData;
        
        console.log('✅ Réservation extraite:', reservationData);
        setReservation(reservationData);
        setLoadedId(id); // Marquer comme chargé
      } catch (err) {
        console.error('Error fetching reservation:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReservation();
  }, [id, user, navigate, loadedId]);

  const getReservationOwnerId = (res) => {
    if (!res) return null;
    return (
      res.owner_id ||
      res.ownerId ||
      res.listing?.owner_id ||
      res.listing?.ownerId ||
      res.listing?.owner?.id ||
      null
    );
  };

  const getReservationRenterId = (res) => {
    if (!res) return null;
    return res.renter_id || res.renterId || res.renter?.id || null;
  };

  const handleBack = () => {
    // Retour intelligent : soit vers les réservations, soit historique
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Détermine si c'est un owner ou renter
      const ownerId = getReservationOwnerId(reservation);
      const userRole = ownerId && ownerId === user?.id ? 'owner' : 'renter';
      if (userRole === 'owner') {
        navigate('/dashboard?tab=reservations');
      } else {
        navigate('/my-reservations');
      }
    }
  };

  const handleDataUpdate = () => {
    // Recharger les données après mise à jour
    window.location.reload();
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Box textAlign="center">
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Chargement de la réservation...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">{error}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <a href="/my-reservations">Retour à mes réservations</a>
          </Typography>
        </Alert>
      </Container>
    );
  }

  if (!reservation) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">
          Réservation introuvable
        </Alert>
      </Container>
    );
  }

  // Détermine le rôle de l'utilisateur
  const ownerId = getReservationOwnerId(reservation);
  const renterId = getReservationRenterId(reservation);
  let userRole = 'renter';

  if (ownerId && user?.id === ownerId) {
    userRole = 'owner';
  } else if (renterId && user?.id === renterId) {
    userRole = 'renter';
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <ReservationDetailsPage
        reservation={reservation}
        currentUser={user}
        userRole={userRole}
        onBack={handleBack}
        onDataUpdate={handleDataUpdate}
        onReservationRefresh={handleDataUpdate}
      />
    </Box>
  );
};

export default ReservationDetailPage;
